import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Input } from '../components/ui/Input';
import { FileInput } from '../components/ui/FileInput';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Sheet, SheetContent, SheetBody } from '../components/ui/Sheet';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import {
  ApiError,
  type ChecklistItem,
  type ChecklistResponse,
  type DmarcReport,
  type DmarcReportDetail,
  type DmarcReportListResponse,
  type DmarcSummaryResponse,
  type KnownSender,
  type KnownSenderListResponse,
  type KnownSenderMatchType,
} from '../types/api';

/** Plan §4a's checklist items — auto items are derived from data (read-only here), manual items are ticked by an admin. */
const CHECKLIST_LABELS: Record<string, string> = {
  full_list_reached_one_day_no_autopause:
    'The full legal list reached in one day without auto-pause',
  dmarc_14_days_no_unknown_sources: '2 weeks of DMARC reports with no unknown legitimate senders',
  mailchimp_unsubscribe_sync_done: 'Unsubscribes reconciled from Mailchimp',
  woo_order_mails_unaffected: 'Woo order emails are unaffected',
  dmarc_policy_pct_100: 'DMARC policy raised to pct=100',
  k2_k3_dkim_removed: 'k2/k3 DKIM keys removed',
  elastic_email_dkim_clarified: 'Elastic Email DKIM clarified (who uses it?)',
  mailchimp_downgraded_to_free: 'Mailchimp downgraded to the free plan',
};

const MATCH_TYPE_LABEL: Record<KnownSenderMatchType, string> = {
  dkim_domain: 'DKIM domain (d=)',
  ip_cidr: 'IP/CIDR',
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function dmarcErrorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError && error.status === 403) {
    return 'Only administrators can change this.';
  }
  if (error instanceof ApiError && error.status === 409) {
    return 'The report has already been uploaded.';
  }
  if (error instanceof ApiError && error.body.message) return error.body.message;
  return fallback;
}

function formatPassRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate * 100)} %`;
}

/**
 * Settings → Mailchimp cutover (P8, docs/decisions.md
 * "DMARC-overgang 28/9"): phase 4 "goodbye to Mailchimp". Everyone can read
 * the DMARC reports, the pass-rate overview and the checklist — only an
 * admin can upload a new report, add/remove a known sender or tick a
 * manual checklist item. Source IPs in a DMARC report are sender
 * infrastructure (SES, Simply websmtp, Mailchimp, ...), not contact
 * data — but an email address is never shown.
 */
export function OvergangFraMailchimpPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [activeReportId, setActiveReportId] = useState<string | null>(null);
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const [senderLabel, setSenderLabel] = useState('');
  const [senderMatchType, setSenderMatchType] = useState<KnownSenderMatchType>('ip_cidr');
  const [senderMatchValue, setSenderMatchValue] = useState('');
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const senderLabelId = useId();
  const senderMatchTypeId = useId();
  const senderMatchValueId = useId();

  const invalidateAll = async () => {
    await queryClient.cancelQueries({ queryKey: ['dmarc-cutover'] });
    await queryClient.invalidateQueries({ queryKey: ['dmarc-cutover'] });
  };

  const { data: reportsData, isLoading: reportsLoading } = useQuery({
    queryKey: ['dmarc-cutover', 'reports'],
    queryFn: () => apiFetch<DmarcReportListResponse>('/dmarc-cutover/reports'),
  });

  const { data: reportDetail } = useQuery({
    queryKey: ['dmarc-cutover', 'report', activeReportId],
    queryFn: () => apiFetch<DmarcReportDetail>(`/dmarc-cutover/reports/${activeReportId}`),
    enabled: activeReportId !== null,
  });

  const { data: summary } = useQuery({
    queryKey: ['dmarc-cutover', 'summary'],
    queryFn: () => apiFetch<DmarcSummaryResponse>('/dmarc-cutover/summary?days=30'),
  });

  const { data: knownSendersData } = useQuery({
    queryKey: ['dmarc-cutover', 'known-senders'],
    queryFn: () => apiFetch<KnownSenderListResponse>('/dmarc-cutover/known-senders'),
  });

  const { data: checklistData } = useQuery({
    queryKey: ['dmarc-cutover', 'checklist'],
    queryFn: () => apiFetch<ChecklistResponse>('/dmarc-cutover/checklist'),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const contentBase64 = await fileToBase64(file);
      return apiFetch<DmarcReport>('/dmarc-cutover/reports', {
        method: 'POST',
        body: { fileName: file.name, contentBase64 },
      });
    },
    onSuccess: (report) => {
      setUploadNotice(`"${report.sourceFileName}" uploaded (${report.orgName}).`);
      return invalidateAll();
    },
  });

  const addSenderMutation = useMutation({
    mutationFn: () =>
      apiFetch<KnownSender>('/dmarc-cutover/known-senders', {
        method: 'POST',
        body: { label: senderLabel, matchType: senderMatchType, matchValue: senderMatchValue },
      }),
    onSuccess: () => {
      setSenderLabel('');
      setSenderMatchValue('');
      return invalidateAll();
    },
  });

  const removeSenderMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: boolean }>(`/dmarc-cutover/known-senders/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(),
  });

  const checklistMutation = useMutation({
    mutationFn: ({ key, checked, note }: { key: string; checked: boolean; note?: string }) =>
      apiFetch<ChecklistItem>(`/dmarc-cutover/checklist/${key}`, {
        method: 'POST',
        body: note !== undefined ? { checked, note } : { checked },
      }),
    onSuccess: () => invalidateAll(),
  });

  async function handleUploadChange(fileList: FileList | null): Promise<void> {
    const file = fileList?.[0];
    if (!file) return;
    await uploadMutation.mutateAsync(file);
  }

  function noteValue(item: ChecklistItem): string {
    return noteDrafts[item.key] ?? item.note ?? '';
  }

  const uploadError = dmarcErrorMessage(uploadMutation.error, 'The report could not be uploaded.');
  const senderError = dmarcErrorMessage(addSenderMutation.error, 'The sender could not be added.');
  const checklistError = dmarcErrorMessage(
    checklistMutation.error,
    'The checklist could not be updated.',
  );

  const activeReport = activeReportId ? (reportDetail ?? null) : null;
  const autoItems = checklistData?.items.filter((i) => i.kind === 'auto') ?? [];
  const manualItems = checklistData?.items.filter((i) => i.kind === 'manual') ?? [];

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Mailchimp cutover</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted max-w-[680px]">
        DMARC reports, pass rate and the cutover checklist (plan §4a). Upload the aggregate DMARC
        reports (XML, .xml.gz or .zip) from Google/Microsoft/Yahoo, watch for unknown legitimate
        senders, and tick off the checklist before cancelling Mailchimp.
      </p>

      {isAdmin ? (
        <Card className="p-4 flex flex-col gap-3 max-w-[680px]">
          <h2 className="text-sm font-semibold m-0">Upload DMARC report</h2>
          <Label htmlFor="dmarc-report-upload">Aggregate report (.xml)</Label>
          <FileInput
            id="dmarc-report-upload"
            accept=".xml,text/xml,application/xml"
            disabled={uploadMutation.isPending}
            onChange={(e) => {
              void handleUploadChange(e.target.files);
              e.target.value = '';
            }}
          />
          {uploadError ? <Alert>{uploadError}</Alert> : null}
          {uploadNotice ? (
            <div role="status" className="text-success-text text-sm">
              {uploadNotice}
            </div>
          ) : null}
        </Card>
      ) : null}

      <Card className="p-4 flex flex-col gap-3 max-w-[680px]">
        <h2 className="text-sm font-semibold m-0">Pass rate (last 30 days)</h2>
        {summary ? (
          <>
            <div className="text-sm">
              {summary.totalCount} messages in total · pass rate {formatPassRate(summary.passRate)}
            </div>
            {summary.byDay.length > 0 ? (
              <ul className="m-0 p-0 list-none flex flex-col gap-0.5 text-xs">
                {summary.byDay.map((d) => (
                  <li key={d.day} className="flex justify-between max-w-[360px]">
                    <span className="text-muted">{d.day}</span>
                    <span>
                      {d.total} · {formatPassRate(d.passRate)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="m-0 text-sm text-muted">No reports in the period yet.</p>
            )}
            {summary.bySender.length > 0 ? (
              <div>
                <h3 className="text-xs font-semibold text-muted m-0 mb-1">
                  Senders (known vs. unknown)
                </h3>
                <ul className="m-0 p-0 list-none flex flex-col gap-0.5 text-xs">
                  {summary.bySender.map((s) => (
                    <li key={s.label} className="flex justify-between max-w-[360px]">
                      <span className={s.known ? '' : 'text-danger-text font-semibold'}>
                        {s.label}
                      </span>
                      <span>{s.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : (
          <div className="text-muted text-sm">Loading…</div>
        )}
      </Card>

      <Card className="p-4 flex flex-col gap-3 max-w-[680px]">
        <h2 className="text-sm font-semibold m-0">Known senders</h2>
        <p className="m-0 text-xs text-muted">
          Used to recognize legitimate mail (SES, Simply websmtp for Woo order emails, Mailchimp, …)
          in the pass-rate overview — everything else is shown as unknown.
        </p>
        {knownSendersData && knownSendersData.items.length > 0 ? (
          <ul className="m-0 p-0 list-none flex flex-col gap-1 text-sm">
            {knownSendersData.items.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 max-w-[560px]">
                <span>
                  {s.label}{' '}
                  <span className="text-xs text-muted">
                    ({MATCH_TYPE_LABEL[s.matchType]}: {s.matchValue})
                  </span>
                </span>
                {isAdmin ? (
                  <Button
                    type="button"
                    variant="danger"
                    disabled={removeSenderMutation.isPending}
                    onClick={() => removeSenderMutation.mutate(s.id)}
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-muted">No known senders added yet.</p>
        )}

        {isAdmin ? (
          <div className="flex flex-col gap-2 border-t border-zinc-200 pt-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={senderLabelId}>Name</Label>
              <Input
                id={senderLabelId}
                value={senderLabel}
                onChange={(e) => setSenderLabel(e.target.value)}
                placeholder="Amazon SES"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={senderMatchTypeId}>Type</Label>
              <select
                id={senderMatchTypeId}
                className="h-[38px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                value={senderMatchType}
                onChange={(e) => setSenderMatchType(e.target.value as KnownSenderMatchType)}
              >
                <option value="ip_cidr">{MATCH_TYPE_LABEL.ip_cidr}</option>
                <option value="dkim_domain">{MATCH_TYPE_LABEL.dkim_domain}</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={senderMatchValueId}>
                {senderMatchType === 'ip_cidr' ? 'CIDR (e.g. 192.0.2.0/24)' : 'Domain (d=)'}
              </Label>
              <Input
                id={senderMatchValueId}
                value={senderMatchValue}
                onChange={(e) => setSenderMatchValue(e.target.value)}
              />
            </div>
            {senderError ? <Alert>{senderError}</Alert> : null}
            <Button
              type="button"
              className="self-end"
              disabled={
                addSenderMutation.isPending || !senderLabel.trim() || !senderMatchValue.trim()
              }
              onClick={() => addSenderMutation.mutate()}
            >
              {addSenderMutation.isPending ? 'Adding…' : 'Add sender'}
            </Button>
          </div>
        ) : null}
      </Card>

      <Card className="p-4 flex flex-col gap-3 max-w-[680px]">
        <h2 className="text-sm font-semibold m-0">Uploaded reports</h2>
        {reportsLoading ? <div className="text-muted text-sm">Loading…</div> : null}
        {reportsData && reportsData.items.length === 0 ? (
          <p className="m-0 text-sm text-muted">No reports uploaded yet.</p>
        ) : null}
        {reportsData && reportsData.items.length > 0 ? (
          <ul className="m-0 p-0 list-none flex flex-col gap-1 text-sm">
            {reportsData.items.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="text-link underline-offset-2 hover:underline"
                  onClick={() => setActiveReportId(r.id)}
                >
                  {r.orgName} — {r.policyP}
                  {r.policyPct !== null ? ` (pct=${r.policyPct})` : ''}
                </button>{' '}
                <span className="text-xs text-muted">({formatDateTime(r.uploadedAt)})</span>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      <Card className="p-4 flex flex-col gap-4 max-w-[680px]">
        <h2 className="text-sm font-semibold m-0">Cutover checklist</h2>
        {checklistError ? <Alert>{checklistError}</Alert> : null}

        <div>
          <h3 className="text-xs font-semibold text-muted m-0 mb-1">
            Calculated automatically from data
          </h3>
          <ul className="m-0 p-0 list-none flex flex-col gap-1.5 text-sm">
            {autoItems.map((item) => (
              <li key={item.key} className="flex items-center gap-2">
                <input type="checkbox" checked={item.checked} disabled readOnly />
                <span>{CHECKLIST_LABELS[item.key] ?? item.key}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold text-muted m-0 mb-1">Manual items</h3>
          <ul className="m-0 p-0 list-none flex flex-col gap-3 text-sm">
            {manualItems.map((item) => (
              <li key={item.key} className="flex flex-col gap-1">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.checked}
                    disabled={!isAdmin || checklistMutation.isPending}
                    onChange={(e) =>
                      checklistMutation.mutate({
                        key: item.key,
                        checked: e.target.checked,
                        note: noteValue(item),
                      })
                    }
                  />
                  <span>{CHECKLIST_LABELS[item.key] ?? item.key}</span>
                </label>
                {isAdmin ? (
                  <Input
                    aria-label={`Note for ${CHECKLIST_LABELS[item.key] ?? item.key}`}
                    className="max-w-[420px] ml-6"
                    value={noteValue(item)}
                    onChange={(e) =>
                      setNoteDrafts((prev) => ({ ...prev, [item.key]: e.target.value }))
                    }
                    onBlur={(e) => {
                      if (e.target.value === (item.note ?? '')) return;
                      checklistMutation.mutate({
                        key: item.key,
                        checked: item.checked,
                        note: e.target.value,
                      });
                    }}
                    placeholder="Note (optional)"
                  />
                ) : item.note ? (
                  <p className="m-0 ml-6 text-xs text-muted">{item.note}</p>
                ) : null}
                {item.checked && item.checkedAt ? (
                  <p className="m-0 ml-6 text-xs text-muted">
                    Ticked by {item.checkedBy} · {formatDateTime(item.checkedAt)}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Sheet
        open={activeReportId !== null}
        onOpenChange={(open) => {
          if (!open) setActiveReportId(null);
        }}
      >
        {activeReport ? (
          <SheetContent
            eyebrow="DMARC report"
            title={activeReport.orgName}
            description={`${formatDateTime(activeReport.dateBegin)} – ${formatDateTime(activeReport.dateEnd)} · policy ${activeReport.policyP}${activeReport.policyPct !== null ? ` (pct=${activeReport.policyPct})` : ''}`}
          >
            <SheetBody>
              <div className="text-xs text-muted">
                Source: {activeReport.sourceFileName} · uploaded{' '}
                {formatDateTime(activeReport.uploadedAt)}
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="pb-1">Source IP</th>
                    <th className="pb-1 text-right">Count</th>
                    <th className="pb-1">Disposition</th>
                    <th className="pb-1">DKIM</th>
                    <th className="pb-1">SPF</th>
                  </tr>
                </thead>
                <tbody>
                  {activeReport.records.map((rec) => (
                    <tr key={rec.id} className="border-t border-zinc-100">
                      <td className="py-1 font-mono">{rec.sourceIp}</td>
                      <td className="py-1 text-right">{rec.count}</td>
                      <td className="py-1">{rec.disposition}</td>
                      <td className="py-1">{rec.dkimResult}</td>
                      <td className="py-1">{rec.spfResult}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SheetBody>
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}
